import { createSelector } from '@reduxjs/toolkit';

import type { SearchOptions } from 'Core/reducers/search/lastSearchOptions';
import type { ReduxStateType, Selector } from 'Core/store';
import type Facet from 'Models/facet';
import { FacetCollection, FacetValue, type FacetValueBase, SearchItemDataCollection } from 'Models/index';
import facetsConfig from 'Models/uiConfig/facetsConfig';
import uiConfig from 'Models/uiConfig/uiConfig';
import { createFacetCollectionSelector, createFacetValueArraySelector } from 'Modules/selectors';
import { Message, type SearchErrorInfo, type ServerModel } from 'Modules/serverApi/types';

type SubFacetLike = Pick<Facet, 'field'> & Partial<Pick<Facet, 'selection'>>;

export const searchLastSearchOptionsSelector = (state: ReduxStateType): SearchOptions =>
  state.search.lastSearchOptions;

export const serverRedirectSelector = (state: ReduxStateType): string | null => state.search.serverRedirect;

// request

export const searchRequestSelector = (state: ReduxStateType): ReduxStateType['search']['request'] =>
  state.search.request;

export const searchRequestSelectionSelector = (state: ReduxStateType): FacetValueBase[] =>
  searchRequestSelector(state).selection;

export const searchRequestFilterQuerySelector = (state: ReduxStateType): string | undefined =>
  searchRequestSelector(state).filterQuery;

export const searchRequestQuerySelector = (state: ReduxStateType): string | undefined =>
  searchRequestSelector(state).query;

const maxCountSelectorCache: Record<string, Selector<number | null>> = {};

export function createMaxCountSelector(field: string): Selector<number | null> {
  if (!maxCountSelectorCache[field]) {
    maxCountSelectorCache[field] = (state) => state.search.request.maxCountByField[field] || null;
  }
  return maxCountSelectorCache[field];
}

// response

const searchResponseSelector = (state: ReduxStateType): ReduxStateType['search']['response'] =>
  state.search.response;

const searchFacetsSelector = createFacetCollectionSelector(
  searchResponseSelector,
  (response: ReduxStateType['search']['response']) => response.facets,
);

function filterByFields<T extends { field: string }>(
  fields: string[],
  ignoreFields?: string[],
): (value: T) => boolean {
  return (value: T) =>
    fields.length ? fields.includes(value.field) : ignoreFields ? !ignoreFields.includes(value.field) : true;
}

export function createFilteredFacetsSelector(
  fields: string[] = [],
  ignoreFields?: string[],
): Selector<FacetCollection> {
  if (!fields.length && !ignoreFields) {
    return searchFacetsSelector;
  }
  const predicate = filterByFields(fields, ignoreFields);
  return (state) => searchFacetsSelector(state).filter(predicate);
}

const facetSelectorCache: Record<string, Selector<Facet | undefined>> = {};

export function createFacetSelector(field: string): Selector<Facet | undefined> {
  if (!facetSelectorCache[field]) {
    facetSelectorCache[field] = createSelector(searchFacetsSelector, (facets) => facets.get(field));
  }
  return facetSelectorCache[field];
}

const noSubFacets: SubFacetLike[] = [];

export function createSubFacetsSelector(field: string): Selector<SubFacetLike[]> {
  const { childFields, singleFacet } = facetsConfig.getChildrenForField(field);

  return singleFacet
    ? createSelector(searchFacetsSelector, (facets): SubFacetLike[] =>
        childFields.map((childField) => facets.get(childField) || { field: childField }),
      )
    : (): SubFacetLike[] => noSubFacets;
}

export function createSearchFacetHasSelectionSelector({
  field,
  selection,
}: {
  field: string;
  selection: FacetValueBase[];
}): Selector<boolean> {
  const subFacetsSelector = createSubFacetsSelector(field);
  return createSelector(
    subFacetsSelector,
    (subFacets: SubFacetLike[]): boolean =>
      !!(selection.length || subFacets.some(({ selection = [] }) => selection.length)),
  );
}

export const searchQuerySelector = (state: ReduxStateType): string | undefined =>
  searchResponseSelector(state).query;

export const searchOriginalQuerySelector = (state: ReduxStateType): string | undefined =>
  searchResponseSelector(state).originalQuery;

export const searchResponseSelectionSelector = createFacetValueArraySelector(
  searchFacetsSelector,
  searchQuerySelector,
  searchRequestFilterQuerySelector,
  (facets: FacetCollection, query: string | undefined, filterQuery: string | undefined): FacetValueBase[] => {
    const selection: FacetValueBase[] = [...facets.selection];

    if (filterQuery) {
      selection.unshift(FacetValue.create({ field: 'filterQuery', term: filterQuery }));
    }
    if (query) {
      selection.unshift(FacetValue.create({ field: 'query', term: query }));
    }

    return selection;
  },
);

export function createFilteredResponseSelectionSelector(
  fields: string[] = [],
  ignoreFields?: string[],
): Selector<FacetValueBase[]> {
  if (!fields.length && !ignoreFields) {
    return searchResponseSelectionSelector;
  }
  const predicate = filterByFields<FacetValueBase>(fields, ignoreFields);
  return createFacetValueArraySelector(searchResponseSelectionSelector, (collection: FacetValueBase[]) =>
    collection.filter(predicate),
  );
}

export function createShouldShowFacetPanelSelector(
  fields: string[],
  ignoreFields?: string[],
): Selector<number> {
  const facetsSelector = createFilteredFacetsSelector(fields, ignoreFields);
  const selectionSelector = createFilteredResponseSelectionSelector(fields, ignoreFields);
  return (state) => facetsSelector(state).length || selectionSelector(state).length;
}

export const searchItemsSelector = (state: ReduxStateType): SearchItemDataCollection =>
  searchResponseSelector(state).items;

export const searchItemsLengthSelector = (state: ReduxStateType): number => searchItemsSelector(state).length;

export const noItemsReceivedSelector = (state: ReduxStateType): boolean => !searchItemsLengthSelector(state);

export const totalHitsSelector = (state: ReduxStateType): number => searchResponseSelector(state).totalHits;

export const allItemsReceivedSelector = (state: ReduxStateType): boolean =>
  searchItemsLengthSelector(state) === totalHitsSelector(state);

export const loadMoreFailedSelector = (state: ReduxStateType): boolean =>
  uiConfig.infiniteScrollEnabled && !totalHitsSelector(state) && !!searchItemsLengthSelector(state);

export const searchResponseErrorSelector = (state: ReduxStateType): SearchErrorInfo | null =>
  searchResponseSelector(state).error ?? null;

export const searchResponseStateSelector = (state: ReduxStateType): string =>
  searchResponseSelector(state).state;

export const searchResponseExtraSelector = (
  state: ReduxStateType,
): (ServerModel.GenericItem & ServerModel.SearchResponseExtra) | null =>
  searchResponseSelector(state).extra ?? null;

export const selectedFacetsCountSelector = (state: ReduxStateType): number =>
  searchFacetsSelector(state).filter((facet) => Boolean(facet.selection.length)).length;

// combined

export const searchPageSizeSelector = (state: ReduxStateType): number =>
  searchRequestSelector(state).pageSize;

export const lastPageNumberSelector = (state: ReduxStateType): number =>
  Math.floor((totalHitsSelector(state) - 1) / searchPageSizeSelector(state));

export const nextPageSizeSelector = (state: ReduxStateType): number =>
  Math.min(searchPageSizeSelector(state), totalHitsSelector(state) - searchItemsLengthSelector(state));

export const pageFromSelector = (state: ReduxStateType): number =>
  uiConfig.infiniteScrollEnabled ? 1 : state.search.request.pageNumber * searchPageSizeSelector(state) + 1;

export const pageToSelector = (state: ReduxStateType): number =>
  Math.min((state.search.request.pageNumber + 1) * searchPageSizeSelector(state), totalHitsSelector(state));

// messages

export const searchResponseMessagesSelector = (
  state: ReduxStateType,
): ReduxStateType['search']['response']['messages'] => searchResponseSelector(state).messages;

export const nonVehicleSpecificMessageSelector = createSelector(searchResponseMessagesSelector, (messages) =>
  messages.includes(Message.NonVehicleSpecificResults),
);

export const nonVehicleMessageSelector = createSelector(searchResponseMessagesSelector, (messages) =>
  messages.includes(Message.NonVehicleResults),
);
